/**
 * ═══ THE BRIDGE TO THE PHONE ═══
 *
 * The mobile app is this same React app inside a Capacitor shell. This file is the only place that
 * knows the difference.
 *
 * ⚠ NOTHING HERE IS IMPORTED AT BUILD TIME. Every Capacitor package is loaded with a dynamic
 * import inside a try, so the web bundle has no dependency on them and the browser build is
 * unaffected — `isNative` is false, every function returns immediately, and nothing is downloaded.
 * A static import would make the web app fail to build until the native packages were installed,
 * which is a coupling with no upside.
 *
 * ⚠ AND EVERY FAILURE IS SWALLOWED. If push registration throws — permission refused, Play Services
 * missing on a cheap handset, Firebase misconfigured — the app must still work. Notifications are a
 * feature; the app not opening is not a trade anyone would make.
 */

/** ⚠ Named, not inlined — see the note at the import site. The shell installs these; the web build
 *  never asks for them. */
const PUSH_MODULE = '@capacitor/push-notifications';
const CAMERA_MODULE = '@capacitor/camera';

/** True only inside the Capacitor shell. ⚠ Read from the global the shell injects, not from a user
 *  agent string — a phone browser is not the app, and a user agent can say anything. */
export function isNative(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return typeof cap?.isNativePlatform === 'function' ? cap.isNativePlatform() : false;
}

export function nativePlatform(): 'Android' | 'Ios' | 'Web' {
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const p = cap?.getPlatform?.();
  return p === 'android' ? 'Android' : p === 'ios' ? 'Ios' : 'Web';
}

type PushToken = { value: string };
type PushAction = { notification?: { data?: Record<string, string> } };

/**
 * Ask for notification permission, register with FCM, and tell the server the token.
 *
 * ⚠ CALLED ON EVERY LAUNCH, not only the first. FCM rotates tokens on its own schedule, and an app
 * that registers once goes quiet the day it rotates — with a switch still saying notifications are
 * on, which is the worst version of that failure.
 *
 * @param register  what to do with the token — normally POST /api/devices
 * @param navigate  where to send the user when they tap a notification
 */
export async function startPush(
  register: (token: string, platform: string) => Promise<unknown>,
  navigate: (path: string) => void,
): Promise<void> {
  if (!isNative()) return;

  try {
    // ⚠ THE SPECIFIER IS A VARIABLE, ON PURPOSE. With a literal, TypeScript resolves it at compile
    // time and the WEB build fails with "cannot find module @capacitor/push-notifications" — a
    // package the web build has no business needing. A variable specifier is opaque to both the
    // type checker and the bundler, which is exactly the decoupling this file exists for.
    const pushMod = await import(/* @vite-ignore */ PUSH_MODULE) as unknown as
      { PushNotifications: NativePush };
    const { PushNotifications } = pushMod;

    const permission = await PushNotifications.checkPermissions();
    let granted = permission.receive === 'granted';
    if (!granted) granted = (await PushNotifications.requestPermissions()).receive === 'granted';

    // ⚠ A refusal is final and silent. Asking again on the next launch is how an app gets deleted,
    // and on Android 13+ the second prompt is not shown anyway.
    if (!granted) return;

    PushNotifications.addListener('registration', (t: PushToken) => {
      void register(t.value, nativePlatform()).catch(() => undefined);
    });

    PushNotifications.addListener('registrationError', () => {
      // Nothing to do and nothing to say. A handset without Play Services simply does not get
      // notifications; the rest of the app is unaffected.
    });

    // ⚠ THE TAP IS THE WHOLE POINT. `data.path` is built by DeepLink on the server and is always a
    // relative path — never an absolute URL, which in a router would be a full page load at best.
    PushNotifications.addListener('pushNotificationActionPerformed', (action: PushAction) => {
      const path = action.notification?.data?.path;
      if (typeof path === 'string' && path.startsWith('/')) navigate(path);
    });

    await PushNotifications.register();
  } catch {
    // The packages are not present (a browser build) or the platform refused. Either way the app
    // carries on without notifications.
  }
}

interface NativePush {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  addListener(event: string, cb: (arg: never) => void): void;
  register(): Promise<void>;
}

/**
 * A photograph from the device camera, as a File.
 *
 * ⚠ RETURNS NULL RATHER THAN THROWING when there is no camera, no permission, or the user backed
 * out. Every caller already has a file picker; this is the better path when it is available, not a
 * replacement, and a throw would mean handling it at four call sites for no gain.
 *
 * ⚠ AND IT RETURNS A REAL File, so the existing upload code is unchanged. A Capacitor-shaped
 * result leaking into StudentPhotosPage would mean two upload paths, which is how the two drift.
 */
export async function takePhoto(fileName = 'photo.jpg'): Promise<File | null> {
  if (!isNative()) return null;

  try {
    const mod = await import(/* @vite-ignore */ CAMERA_MODULE) as unknown as {
      Camera: { getPhoto(o: Record<string, unknown>): Promise<{ webPath?: string }> };
      CameraResultType: Record<string, string>;
      CameraSource: Record<string, string>;
    };

    const shot = await mod.Camera.getPhoto({
      resultType: mod.CameraResultType.Uri,
      source: mod.CameraSource.Camera,
      // ⚠ 1200px and 80% quality, matching what the server says about ID cards: anything wider is
      // larger than a card needs and only makes the printed sheet slower to render.
      width: 1200,
      quality: 80,
      correctOrientation: true,
      // ⚠ JPEG, not the phone's default. An iPhone shoots HEIC unless told otherwise, and the
      // server refuses HEIC with a message about changing a camera setting — which a user cannot
      // act on inside our own app.
      format: 'jpeg',
    });

    if (!shot.webPath) return null;
    const blob = await (await fetch(shot.webPath)).blob();
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  } catch {
    return null;
  }
}
